<main class="grow" role="content">
  <div class="kt-container-fixed swap-detail-container py-5 lg:py-7.5">
    <div class="kt-card">
      <div class="kt-card-header min-h-16 flex-wrap gap-3">
        <div class="flex min-w-0 flex-col gap-1">
          <div class="text-xs text-secondary-foreground">Swaps / Details</div>
          <h1 class="kt-card-title">Swap Details</h1>
        </div>

        <div class="inline-flex items-center gap-2.5 flex-wrap ms-auto">
          <span class="kt-badge kt-badge-outline">
            Last Modified By: {{ detail()?.swapExtLocal?.modifiedBy || '' }}
            @if (detail()?.swapExtLocal?.dateModified) {
              &nbsp;|&nbsp;{{ formatDateTimeOrBlank(detail()?.swapExtLocal?.dateModified || '') }}
            }
          </span>

          @if (!detail()?.isNew) {
            <button type="button" class="kt-btn kt-btn-sm kt-btn-destructive">Delete</button>
          }

          <button
            type="button"
            class="kt-btn kt-btn-sm kt-btn-light"
            (click)="onSave()"
            [disabled]="isSaving() || !hasUnsavedChanges()"
          >
            @if (!isSaving()) {
              Save
            } @else {
              Saving...
            }
          </button>
        </div>
      </div>

      <div class="kt-card-content swap-detail-content-shell p-5 lg:p-7.5">
        @if (isLoading()) {
          <section class="kt-card swap-detail-readonly-card">
            <div class="kt-card-content p-6">
              <div class="catalog-loading" role="status" aria-live="polite" aria-label="Loading swap detail">
                <span class="catalog-loading-spinner" aria-hidden="true"></span>
                <span class="catalog-loading-text">Loading...</span>
              </div>
            </div>
          </section>
        }

        @if (errorMessage()) {
          <section class="kt-alert kt-alert-destructive flex items-center justify-between gap-3">
            <span>{{ errorMessage() }}</span>
            <button type="button" class="kt-btn kt-btn-sm kt-btn-light" (click)="onRetry()">Retry</button>
          </section>
        }

        @if (saveError()) {
          <section class="kt-alert kt-alert-destructive">
            {{ saveError() }}
          </section>
        }

        @if (saveSuccessMessage()) {
          <section class="kt-alert kt-alert-success">
            {{ saveSuccessMessage() }}
          </section>
        }

        @if (!isLoading() && !errorMessage() && detail(); as detailData) {
          <section class="kt-card swap-detail-readonly-card">
            <div class="kt-card-content p-5">
              <div class="swap-detail-legs-section">
                <div class="kt-table-wrapper kt-scrollable">
                  <table class="kt-table">
                    <thead>
                      <tr>
                        <th></th>
                        <th>Currency</th>
                        <th>Notional</th>
                        <th>Index</th>
                        <th>Term</th>
                        <th>Rate</th>
                        <th>Spread</th>
                        <th>Product / Formula</th>
                        <th>RFR Fall Spread</th>
                        <th>Location</th>
                        <th>Company</th>
                        <th>Desk</th>
                        <th>Book</th>
                        <th>Folder</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>PAY</td>
                        <td>{{ detailData.pay.ccy || '-' }}</td>
                        <td>{{ formatNumber(detailData.pay.notional, 2) }}</td>
                        <td>{{ detailData.pay.indexName || '-' }}</td>
                        <td>{{ detailData.pay.termRateSpread || '-' }}</td>
                        <td>{{ detailData.pay.rate || '-' }}</td>
                        <td>{{ detailData.pay.spread || '-' }}</td>
                        <td>
                          <span class="swap-formula-cell">
                            <span>{{ detailData.pay.assetFormula || '-' }}</span>
                            @if (hasPayFormulas()) {
                              <button
                                type="button"
                                class="kt-btn kt-btn-icon kt-btn-sm kt-btn-outline"
                                aria-label="View PAY formulas"
                                (click)="openPayFormulasModal()"
                              >
                                <i class="ki-filled ki-eye text-sm" aria-hidden="true"></i>
                              </button>
                            }
                          </span>
                        </td>
                        <td>{{ detailData.pay.rfrFallbackSpread || '-' }}</td>
                        <td>{{ detailData.pay.location || '-' }}</td>
                        <td>{{ detailData.pay.company || '-' }}</td>
                        <td>{{ detailData.pay.desk || '-' }}</td>
                        <td>{{ detailData.pay.book || '-' }}</td>
                        <td>{{ detailData.pay.folder || '-' }}</td>
                      </tr>
                      <tr>
                        <td>RECEIVE</td>
                        <td>{{ detailData.receive.ccy || '-' }}</td>
                        <td>{{ formatNumber(detailData.receive.notional, 2) }}</td>
                        <td>{{ detailData.receive.indexName || '-' }}</td>
                        <td>{{ detailData.receive.termRateSpread || '-' }}</td>
                        <td>{{ detailData.receive.rate || '-' }}</td>
                        <td>{{ detailData.receive.spread || '-' }}</td>
                        <td>
                          <span class="swap-formula-cell">
                            <span>{{ detailData.receive.assetFormula || '-' }}</span>
                            @if (hasReceiveFormulas()) {
                              <button
                                type="button"
                                class="kt-btn kt-btn-icon kt-btn-sm kt-btn-outline"
                                aria-label="View RECEIVE formulas"
                                (click)="openReceiveFormulasModal()"
                              >
                                <i class="ki-filled ki-eye text-sm" aria-hidden="true"></i>
                              </button>
                            }
                          </span>
                        </td>
                        <td>{{ detailData.receive.rfrFallbackSpread || '-' }}</td>
                        <td>{{ detailData.receive.location || '-' }}</td>
                        <td>{{ detailData.receive.company || '-' }}</td>
                        <td>{{ detailData.receive.desk || '-' }}</td>
                        <td>{{ detailData.receive.book || '-' }}</td>
                        <td>{{ detailData.receive.folder || '-' }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <div class="swap-detail-readonly-rows">
                <div class="swap-detail-readonly-row swap-detail-readonly-row-three">
                  <div class="swap-detail-readonly-item">
                    <span class="swap-detail-readonly-label">Trade Date</span>
                    <span class="swap-detail-readonly-value">{{ formatDate(detailData.tradeDate) }}</span>
                  </div>
                  <div class="swap-detail-readonly-item">
                    <span class="swap-detail-readonly-label">Settlement Date</span>
                    <span class="swap-detail-readonly-value">{{ formatDate(detailData.settlementDate) }}</span>
                  </div>
                  <div class="swap-detail-readonly-item">
                    <span class="swap-detail-readonly-label">Maturity Date</span>
                    <span class="swap-detail-readonly-value">{{ formatDate(detailData.maturityDate) }}</span>
                  </div>
                </div>

                <div class="swap-detail-readonly-row swap-detail-readonly-row-four">
                  <div class="swap-detail-readonly-item">
                    <span class="swap-detail-readonly-label">Deal ID</span>
                    <span class="swap-detail-readonly-value">{{ detailData.dealId || '-' }}</span>
                  </div>
                  <div class="swap-detail-readonly-item">
                    <span class="swap-detail-readonly-label">Structure ID</span>
                    <span class="swap-detail-readonly-value">{{ detailData.structureId || '-' }}</span>
                  </div>
                  <div class="swap-detail-readonly-item">
                    <span class="swap-detail-readonly-label">Trade ID</span>
                    <span class="swap-detail-readonly-value">{{ detailData.tradeId || '-' }}</span>
                  </div>
                  <div class="swap-detail-readonly-item">
                    <span class="swap-detail-readonly-label">Security ID</span>
                    <span class="swap-detail-readonly-value">
                      @if (detailData.securityId) {
                        <a
                          class="text-primary hover:underline"
                          [routerLink]="['/bond-definition', detailData.securityId]"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {{ detailData.securityId }}
                        </a>
                      } @else {
                        -
                      }
                    </span>
                  </div>
                </div>

                <div class="swap-detail-readonly-row swap-detail-readonly-row-four">
                  <div class="swap-detail-readonly-item">
                    <span class="swap-detail-readonly-label">RFR Status</span>
                    <span class="swap-detail-readonly-value">{{ detailData.rfrStatus || '-' }}</span>
                  </div>
                  <div class="swap-detail-readonly-item">
                    <span class="swap-detail-readonly-label">Conversion Trade Date</span>
                    <span class="swap-detail-readonly-value">{{ formatDate(detailData.rfrTradeDate) }}</span>
                  </div>
                  <div class="swap-detail-readonly-item">
                    <span class="swap-detail-readonly-label">Fallback Rec Eff Date</span>
                    <span class="swap-detail-readonly-value">{{ formatDate(detailData.rfrEffDateRec) }}</span>
                  </div>
                  <div class="swap-detail-readonly-item">
                    <span class="swap-detail-readonly-label">Fallback Pay Eff Date</span>
                    <span class="swap-detail-readonly-value">{{ formatDate(detailData.rfrEffDatePay) }}</span>
                  </div>
                </div>

                <div class="swap-detail-readonly-row">
                  <div class="swap-detail-readonly-item">
                    <span class="swap-detail-readonly-label">TMS User (Data Entry)</span>
                    <span class="swap-detail-readonly-value">{{ detailData.doneUser || '-' }}</span>
                  </div>
                </div>
              </div>

              <div class="swap-detail-counterparty">
                <span class="swap-detail-readonly-label">Counterparty</span>
                <span class="swap-detail-readonly-value">{{ detailData.counterpartyName || '-' }}</span>
              </div>
            </div>
          </section>

          @if (detailData.isNew) {
            <section class="kt-card swap-detail-gate-card">
              <div class="kt-card-content p-6">
                <h2 class="text-base font-semibold">Register this swap in FRIDA</h2>
                <p class="mt-2 text-sm text-secondary-foreground">
                  You are about to register information about this swap in FRIDA's database. Do you want to proceed?
                </p>

                @if (initializeError()) {
                  <div class="kt-alert kt-alert-destructive mt-4">
                    {{ initializeError() }}
                  </div>
                }

                <div class="mt-5">
                  <button
                    type="button"
                    class="kt-btn kt-btn-primary"
                    (click)="onProceedWithNewSwap()"
                    [disabled]="isInitializing()"
                  >
                    @if (!isInitializing()) {
                      Proceed
                    } @else {
                      Processing...
                    }
                  </button>
                </div>
              </div>
            </section>
          } @else {
          <section class="kt-card swap-detail-tabs-card">
            <div class="kt-card-header min-h-14">
              <div
                #tabsRoot
                id="swap_detail_tabs"
                class="kt-tabs kt-tabs-line swap-detail-tabs"
                data-kt-tabs="true"
                role="tablist"
                aria-label="Swap detail tabs"
              >
                @for (section of sections; track section.id; let index = $index) {
                  <button
                    type="button"
                    role="tab"
                    class="kt-tab-toggle"
                    [class.active]="index === 0"
                    [attr.data-kt-tab-toggle]="'#' + section.panelId"
                    [attr.aria-selected]="index === 0"
                  >
                    <i class="ki-filled" [class]="'ki-filled ' + section.iconClass" aria-hidden="true"></i>
                    {{ section.label }}
                  </button>
                }
              </div>
            </div>

            <div class="kt-card-content p-5">
              @for (section of sections; track section.id; let index = $index) {
                <section class="swap-detail-tab-panel" [class.hidden]="index !== 0" [id]="section.panelId">
                  @if (section.id === 'swap-details') {
                    <section class="swap-details-section">
                      <div class="swap-detail-form-grid swap-detail-form-grid-compact swap-detail-controls-compact">
                        <div class="swap-detail-form-field">
                          <label class="kt-form-label" for="swapPurposeId">Purpose</label>
                          <select
                            id="swapPurposeId"
                            class="kt-select"
                            [value]="purposeId() === null ? '' : purposeId()!.toString()"
                            (change)="onPurposeChange($any($event.target).value)"
                          >
                            <option value="">-</option>
                            @for (purpose of detailData.swapExtLocal.purposes; track purpose.id) {
                              <option [value]="purpose.id.toString()" [selected]="isPurposeSelected(purpose.id)">
                                {{ purpose.name }}
                              </option>
                            }
                          </select>
                        </div>

                        <div class="swap-detail-form-field">
                          <label class="kt-form-label" for="swapPurposeSubtypeId">Purpose Subtype</label>
                          <select
                            id="swapPurposeSubtypeId"
                            class="kt-select"
                            [value]="purposeSubtypeId() === null ? '' : purposeSubtypeId()!.toString()"
                            (change)="onPurposeSubtypeChange($any($event.target).value)"
                          >
                            <option value="">-</option>
                            @for (subtype of filteredPurposeSubtypes(); track subtype.id) {
                              <option [value]="subtype.id.toString()" [selected]="isPurposeSubtypeSelected(subtype.id)">
                                {{ subtype.name }}
                              </option>
                            }
                          </select>
                        </div>
                      </div>
                    </section>

                    <section class="swap-details-section">
                      <h3 class="swap-details-section-title">Other</h3>

                      <div class="swap-detail-form-grid swap-detail-form-grid-compact swap-detail-controls-compact">
                        <div class="swap-detail-form-field">
                          <label class="kt-form-label" for="swapLegSelect">Swap Leg</label>
                          <select id="swapLegSelect" class="kt-select" [value]="swapLeg()" (change)="onSwapLegChange($any($event.target).value)">
                            <option value="PAY">PAY</option>
                            <option value="RECEIVE">RECEIVE</option>
                          </select>
                        </div>

                        <div class="swap-detail-form-field">
                          <label class="kt-form-label" for="swapFeeCurrency">CCY</label>
                          <input id="swapFeeCurrency" class="kt-input" type="text" [value]="tFeeCurrency()" disabled />
                        </div>

                        <div class="swap-detail-form-field">
                          <label class="kt-form-label" for="swapBpsPerYear">BPS per year</label>
                          <input
                            id="swapBpsPerYear"
                            class="kt-input"
                            type="number"
                            step="any"
                            [value]="bpsPerYear()"
                            (input)="onBpsPerYearChange($any($event.target).value)"
                          />
                        </div>

                        <div class="swap-detail-form-field">
                          <label class="kt-form-label" for="swapTheoreticalFee">{{ legOtherLabel() }}</label>
                          <input
                            id="swapTheoreticalFee"
                            class="kt-input"
                            type="number"
                            step="any"
                            [value]="swapTheoreticalFee()"
                            (input)="onSwapTheoreticalFeeChange($any($event.target).value)"
                          />
                        </div>
                      </div>
                    </section>

                    <section class="swap-details-section">
                      <div class="flex items-center justify-between gap-2">
                        <h3 class="swap-details-section-title mb-0">Swap Charges (e.g. CVA, FVA, Execution)</h3>
                        <button
                          type="button"
                          class="kt-btn kt-btn-sm kt-btn-icon kt-btn-outline"
                          data-kt-modal-toggle="#swap_detail_fee_modal"
                          [disabled]="isMutatingSwapFee()"
                          (click)="resetSwapFeeForm()"
                          aria-label="Add swap charge"
                        >
                          <i class="ki-filled ki-plus" aria-hidden="true"></i>
                        </button>
                      </div>
                      <div class="kt-table-wrapper kt-scrollable">
                        <table class="kt-table swap-detail-unified-table swap-detail-last-column-right">
                          <thead>
                            <tr>
                              <th>Fee Type</th>
                              <th>Value</th>
                              <th>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            @if (detailData.swapFees.length === 0) {
                              <tr>
                                <td colspan="3" class="kt-datatable-empty-msg text-secondary-foreground py-6">
                                  No swap charges available.
                                </td>
                              </tr>
                            } @else {
                              @for (fee of detailData.swapFees; track trackFee($index, fee)) {
                                <tr>
                                  <td>{{ fee.feeTypeName || '-' }}</td>
                                  <td>{{ formatNumber(fee.value, 4) }}</td>
                                  <td>
                                    <span class="inline-flex items-center justify-end gap-2 w-full">
                                      <button
                                        type="button"
                                        class="kt-btn kt-btn-sm kt-btn-icon kt-btn-outline"
                                        data-kt-modal-toggle="#swap_detail_fee_modal"
                                        [disabled]="isMutatingSwapFee()"
                                        (click)="startEditSwapFee(fee)"
                                        aria-label="Edit swap charge"
                                      >
                                        <i class="ki-filled ki-pencil" aria-hidden="true"></i>
                                      </button>
                                      <button
                                        type="button"
                                        class="kt-btn kt-btn-sm kt-btn-icon kt-btn-outline"
                                        [disabled]="isMutatingSwapFee()"
                                        (click)="requestDeleteSwapFee(fee)"
                                        aria-label="Delete swap charge"
                                      >
                                        <i class="ki-filled ki-trash" aria-hidden="true"></i>
                                      </button>
                                    </span>
                                  </td>
                                </tr>
                              }
                            }
                          </tbody>
                        </table>
                      </div>
                    </section>
                  }

                  @if (section.id === 'attached-documentation') {
                    <div class="flex items-center justify-end gap-2">
                      <button
                        type="button"
                        class="kt-btn kt-btn-sm kt-btn-icon kt-btn-outline"
                        data-kt-modal-toggle="#swap_detail_add_document_modal"
                        [disabled]="isMutatingDocument()"
                        (click)="resetAddDocumentForm()"
                        aria-label="Add documentation"
                      >
                        <i class="ki-filled ki-plus" aria-hidden="true"></i>
                      </button>
                    </div>

                    <div class="kt-table-wrapper kt-scrollable">
                      <table class="kt-table swap-detail-unified-table swap-detail-last-column-right">
                        <thead>
                          <tr>
                            <th>Description</th>
                            <th>Type</th>
                            <th>Filename</th>
                            <th>Source</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          @if (detailData.documentsAttached.length === 0) {
                            <tr>
                              <td colspan="5" class="kt-datatable-empty-msg text-secondary-foreground py-6">
                                No attached documentation.
                              </td>
                            </tr>
                          } @else {
                            @for (document of detailData.documentsAttached; track trackDocument($index, document)) {
                              <tr>
                                <td>
                                  <app-document-description-cell
                                    [description]="document.description"
                                    [documentLabel]="document.filename"
                                    [disabled]="isMutatingDocument()"
                                    [saving]="describingDocumentId() === document.id"
                                    (descriptionChange)="updateDocumentDescription(document, $event)"
                                  />
                                </td>
                                <td>{{ document.type || '-' }}</td>
                                <td>
                                  <app-document-name-cell
                                    [filename]="document.filename"
                                    [link]="document.ezShareLink"
                                    [disabled]="isMutatingDocument()"
                                    [saving]="renamingDocumentId() === document.id"
                                    [siblingNames]="documentFilenames(detailData.documentsAttached)"
                                    (rename)="renameDocument(document, $event)"
                                  />
                                </td>
                                <td>{{ document.source || '-' }}</td>
                                <td>
                                  <span class="inline-flex items-center justify-end gap-2 w-full">
                                    <button
                                      type="button"
                                      class="kt-btn kt-btn-sm kt-btn-icon kt-btn-outline"
                                      data-kt-modal-toggle="#swap_detail_add_document_modal"
                                      [disabled]="isMutatingDocument()"
                                      (click)="startEditDocumentRow(document)"
                                      aria-label="Edit documentation"
                                    >
                                      <i class="ki-filled ki-pencil" aria-hidden="true"></i>
                                    </button>
                                    <button
                                      type="button"
                                      class="kt-btn kt-btn-sm kt-btn-icon kt-btn-outline"
                                      data-kt-modal-toggle="#swap_detail_delete_document_modal"
                                      [disabled]="isMutatingDocument()"
                                      (click)="requestDeleteDocumentRow(document)"
                                      aria-label="Delete documentation"
                                    >
                                      <i class="ki-filled ki-trash" aria-hidden="true"></i>
                                    </button>
                                  </span>
                                </td>
                              </tr>
                            }
                          }
                        </tbody>
                      </table>
                    </div>
                  }

                  @if (section.id === 'actions') {
                    <div class="flex items-center justify-end gap-2">
                      <button
                        type="button"
                        class="kt-btn kt-btn-sm kt-btn-icon kt-btn-outline"
                        data-kt-modal-toggle="#swap_detail_action_modal"
                        [disabled]="isMutatingSwapAction()"
                        (click)="resetSwapActionForm()"
                        aria-label="Add action"
                      >
                        <i class="ki-filled ki-plus" aria-hidden="true"></i>
                      </button>
                    </div>
                    <div class="kt-table-wrapper kt-scrollable">
                      <table class="kt-table swap-detail-unified-table swap-detail-last-column-right">
                        <thead>
                          <tr>
                            <th>Action Type</th>
                            <th>Trade Date</th>
                            <th>Effective Date</th>
                            <th>Comments</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          @if (detailData.swapActions.length === 0) {
                            <tr>
                              <td colspan="5" class="kt-datatable-empty-msg text-secondary-foreground py-6">
                                No actions available.
                              </td>
                            </tr>
                          } @else {
                            @for (action of detailData.swapActions; track trackAction($index, action)) {
                              <tr>
                                <td>{{ action.actionTypeName || '-' }}</td>
                                <td>{{ formatDate(action.tradeDate) }}</td>
                                <td>{{ formatDate(action.effectiveDate) }}</td>
                                <td>
                                  <textarea
                                    class="kt-textarea swap-detail-table-textarea"
                                    rows="3"
                                    readonly
                                  >{{ action.comments || '-' }}</textarea>
                                </td>
                                <td>
                                  <span class="inline-flex items-center justify-end gap-2 w-full">
                                  <button
                                    type="button"
                                    class="kt-btn kt-btn-sm kt-btn-icon kt-btn-outline"
                                    data-kt-modal-toggle="#swap_detail_action_modal"
                                    [disabled]="isMutatingSwapAction()"
                                    (click)="startEditSwapAction(action)"
                                    aria-label="Edit action"
                                  >
                                    <i class="ki-filled ki-pencil" aria-hidden="true"></i>
                                  </button>
                                  <button
                                    type="button"
                                    class="kt-btn kt-btn-sm kt-btn-icon kt-btn-outline"
                                    [disabled]="isMutatingSwapAction()"
                                    (click)="requestDeleteSwapAction(action)"
                                    aria-label="Delete action"
                                  >
                                    <i class="ki-filled ki-trash" aria-hidden="true"></i>
                                  </button>
                                  </span>
                                </td>
                              </tr>
                            }
                          }
                        </tbody>
                      </table>
                    </div>
                  }

                  @if (section.id === 'notes') {
                    <div class="flex items-center justify-end gap-2">
                      <button
                        type="button"
                        class="kt-btn kt-btn-sm kt-btn-icon kt-btn-outline"
                        data-kt-modal-toggle="#swap_detail_add_note_modal"
                        [disabled]="isMutatingNote()"
                        (click)="resetAddNoteForm()"
                        aria-label="Add note"
                      >
                        <i class="ki-filled ki-plus" aria-hidden="true"></i>
                      </button>
                    </div>
                    <div class="kt-table-wrapper kt-scrollable">
                      <table class="kt-table swap-detail-unified-table swap-detail-last-column-right">
                        <thead>
                          <tr>
                            <th>Note</th>
                            <th>Modified By</th>
                            <th>Date Modified</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          @if (detailData.notes.length === 0) {
                            <tr>
                              <td colspan="4" class="kt-datatable-empty-msg text-secondary-foreground py-6">
                                No notes available.
                              </td>
                            </tr>
                          } @else {
                            @for (note of detailData.notes; track trackNote($index, note)) {
                              <tr>
                                <td>{{ note.description || '-' }}</td>
                                <td>{{ note.modifiedBy || '-' }}</td>
                                <td>{{ formatDateTime(note.dateModified) }}</td>
                                <td>
                                  <span class="inline-flex items-center justify-end gap-2 w-full">
                                    <button
                                      type="button"
                                      class="kt-btn kt-btn-sm kt-btn-icon kt-btn-outline"
                                      data-kt-modal-toggle="#swap_detail_add_note_modal"
                                      [disabled]="isMutatingNote()"
                                      (click)="startEditNoteRow(note)"
                                      aria-label="Edit note"
                                    >
                                      <i class="ki-filled ki-pencil" aria-hidden="true"></i>
                                    </button>
                                    <button
                                      type="button"
                                      class="kt-btn kt-btn-sm kt-btn-icon kt-btn-outline"
                                      [disabled]="isMutatingNote()"
                                      (click)="requestDeleteNoteRow(note)"
                                      aria-label="Delete note"
                                    >
                                      <i class="ki-filled ki-trash" aria-hidden="true"></i>
                                    </button>
                                  </span>
                                </td>
                              </tr>
                            }
                          }
                        </tbody>
                      </table>
                    </div>
                  }
                </section>
              }
            </div>
          </section>
          }
        }
      </div>
    </div>
  </div>
</main>

<app-catalog-crud-modal
  modalId="swap_detail_fee_modal"
  [title]="swapFeeModalTitle()"
  [submitLabel]="swapFeeModalSubmitLabel()"
  [submitting]="isMutatingSwapFee()"
  [formError]="swapFeeFormError()"
  (submit)="submitSwapFee()"
  (cancel)="resetSwapFeeForm()"
>
  <div modal-body>
    <div class="grid gap-3">
      <div class="grid gap-1.5">
        <label class="kt-form-label" for="swap-fee-bps-id">Fee Type</label>
        <select
          id="swap-fee-bps-id"
          class="kt-select"
          [value]="swapFeeForm().feeBpsId"
          (change)="updateSwapFeeField('feeBpsId', $any($event.target).value)"
        >
          <option value="">-</option>
          @for (feeType of swapFeeTypes(); track feeType.id) {
            <option [value]="feeType.id.toString()">{{ feeType.name }}</option>
          }
        </select>
      </div>

      <div class="grid gap-1.5">
        <label class="kt-form-label" for="swap-fee-value">Value</label>
        <input
          id="swap-fee-value"
          type="number"
          class="kt-input"
          step="any"
          [value]="swapFeeForm().value"
          (input)="updateSwapFeeField('value', $any($event.target).value)"
        />
      </div>
    </div>
  </div>
</app-catalog-crud-modal>

<app-catalog-delete-confirm-modal
  modalId="swap_detail_delete_fee_modal"
  title="Delete Swap Charge"
  [message]="'Delete swap charge ' + (pendingSwapFeeDeleteName() || '-') + '?'"
  confirmLabel="Delete"
  [confirming]="isMutatingSwapFee()"
  (confirm)="confirmDeleteSwapFee()"
  (cancel)="cancelDeleteSwapFee()"
></app-catalog-delete-confirm-modal>

<app-catalog-crud-modal
  modalId="swap_detail_action_modal"
  [title]="swapActionModalTitle()"
  [submitLabel]="swapActionModalSubmitLabel()"
  [submitting]="isMutatingSwapAction()"
  [formError]="swapActionFormError()"
  (submit)="submitSwapAction()"
  (cancel)="resetSwapActionForm()"
>
  <div modal-body>
    <div class="grid gap-3 md:grid-cols-2">
      <div class="grid gap-1.5">
        <label class="kt-form-label" for="swap-action-trade-date">Trade Date</label>
        <input
          id="swap-action-trade-date"
          type="date"
          class="kt-input"
          [value]="swapActionForm().tradeDate"
          (input)="updateSwapActionField('tradeDate', $any($event.target).value)"
        />
      </div>

      <div class="grid gap-1.5">
        <label class="kt-form-label" for="swap-action-effective-date">Effective Date</label>
        <input
          id="swap-action-effective-date"
          type="date"
          class="kt-input"
          [value]="swapActionForm().effectiveDate"
          (input)="updateSwapActionField('effectiveDate', $any($event.target).value)"
        />
      </div>

      <div class="grid gap-1.5">
        <label class="kt-form-label" for="swap-action-type-id">Action Type</label>
        <select
          id="swap-action-type-id"
          class="kt-select"
          [value]="swapActionForm().actionTypeId"
          (change)="updateSwapActionField('actionTypeId', $any($event.target).value)"
        >
          <option value="">-</option>
          @for (actionType of swapActionTypes(); track actionType.id) {
            <option [value]="actionType.id.toString()">{{ actionType.name }}</option>
          }
        </select>
      </div>

      <div class="grid gap-1.5">
        <label class="kt-form-label" for="swap-action-description">Description</label>
        <input
          id="swap-action-description"
          type="text"
          class="kt-input"
          [value]="swapActionForm().actionTypeDescription"
          disabled
        />
      </div>

      <div class="grid gap-1.5 md:col-span-2">
        <label class="kt-form-label" for="swap-action-comments">Comments</label>
        <textarea
          id="swap-action-comments"
          class="kt-textarea"
          rows="4"
          [value]="swapActionForm().comments"
          (input)="updateSwapActionField('comments', $any($event.target).value)"
        ></textarea>
      </div>
    </div>
  </div>
</app-catalog-crud-modal>

<app-catalog-delete-confirm-modal
  modalId="swap_detail_delete_action_modal"
  title="Delete Action"
  [message]="'Delete action ' + (pendingSwapActionDeleteName() || '-') + '?'"
  confirmLabel="Delete"
  [confirming]="isMutatingSwapAction()"
  (confirm)="confirmDeleteSwapAction()"
  (cancel)="cancelDeleteSwapAction()"
></app-catalog-delete-confirm-modal>

<app-catalog-crud-modal
  modalId="swap_detail_add_document_modal"
  [title]="documentModalTitle()"
  [submitLabel]="documentModalSubmitLabel()"
  [submitting]="isMutatingDocument()"
  [formError]="documentFormError()"
  (submit)="addOrUpdateDocument()"
>
  <div modal-body>
    <div class="grid gap-3">
      @if (editingDocumentId() === null) {
        <div class="grid gap-1.5">
          <label class="kt-form-label" for="swap-detail-document-files">Files</label>
          <input
            #documentFilesInput
            id="swap-detail-document-files"
            type="file"
            class="kt-input"
            accept=".doc,.msg,.pdf,.xls"
            multiple
            [disabled]="isMutatingDocument()"
            (change)="onDocumentFilesSelected($event)"
          />
          <p class="text-xs text-secondary-foreground">Allowed types: .doc, .msg, .pdf, .xls</p>
        </div>

        @if (documentUploadEntries().length > 0) {
          <div class="grid gap-1">
            <p class="text-sm font-medium">Selected files</p>
            <app-document-upload-list [entries]="documentUploadEntries()" />
          </div>
        }
      } @else {
        <div class="grid gap-1.5">
          <label class="kt-form-label" for="swap-detail-document-filename">Filename</label>
          <input
            id="swap-detail-document-filename"
            type="text"
            class="kt-input"
            [value]="addDocumentForm().filename"
            (input)="updateAddDocumentField('filename', $any($event.target).value)"
          />
        </div>

        <div class="grid gap-1.5">
          <label class="kt-form-label" for="swap-detail-document-description">Description</label>
          <input
            id="swap-detail-document-description"
            type="text"
            class="kt-input"
            [value]="addDocumentForm().description"
            (input)="updateAddDocumentField('description', $any($event.target).value)"
          />
        </div>
      }
    </div>
  </div>
</app-catalog-crud-modal>

<app-catalog-delete-confirm-modal
  modalId="swap_detail_delete_document_modal"
  title="Delete Document"
  [message]="'Delete document: ' + (pendingDocumentDeletePreview() || '-') + '?'"
  confirmLabel="Delete"
  [confirming]="isMutatingDocument()"
  (confirm)="confirmDeleteDocumentRow()"
  (cancel)="cancelDeleteDocumentRow()"
></app-catalog-delete-confirm-modal>

<app-catalog-crud-modal
  modalId="swap_detail_add_note_modal"
  [title]="noteModalTitle()"
  [submitLabel]="noteModalSubmitLabel()"
  [submitting]="isMutatingNote()"
  [formError]="noteFormError()"
  (submit)="addNoteRow()"
  (cancel)="resetAddNoteForm()"
>
  <div modal-body>
    <div class="grid gap-1.5">
      <label class="kt-form-label" for="swap-add-note-text">Note</label>
      <textarea
        id="swap-add-note-text"
        class="kt-textarea"
        rows="4"
        [value]="addNoteForm().note"
        (input)="updateAddNoteField('note', $any($event.target).value)"
      ></textarea>
    </div>
  </div>
</app-catalog-crud-modal>

<app-catalog-delete-confirm-modal
  modalId="swap_detail_delete_note_modal"
  title="Delete Note"
  [message]="'Delete note: ' + (pendingNoteDeletePreview() || '-') + '?'"
  confirmLabel="Delete"
  [confirming]="isMutatingNote()"
  (confirm)="confirmDeleteNoteRow()"
  (cancel)="cancelDeleteNoteRow()"
></app-catalog-delete-confirm-modal>

<app-catalog-crud-modal
  modalId="swap_detail_formulas_modal"
  [title]="selectedFormulasTitle() || 'Formulas'"
  submitLabel=""
  cancelLabel="Close"
  [showSubmit]="false"
  [submitting]="false"
  [formError]="''"
  widthClass="max-w-[900px]"
>
  <div modal-body>
    @if (selectedFormulas().length === 0) {
      <p class="swap-empty-state">No formulas available.</p>
    } @else {
      <div class="kt-table-wrapper kt-scrollable">
        <table class="kt-table">
          <thead>
            <tr>
              <th>Apply Date</th>
              <th>Formula</th>
            </tr>
          </thead>
          <tbody>
            @for (formula of selectedFormulas(); track trackFormula($index, formula)) {
              <tr>
                <td>{{ formatDateTime(formula.applyDate) }}</td>
                <td>{{ formula.formula || '-' }}</td>
              </tr>
            }
          </tbody>
        </table>
      </div>
    }
  </div>
</app-catalog-crud-modal>

<button
  type="button"
  class="hidden"
  aria-hidden="true"
  tabindex="-1"
  data-kt-modal-toggle="#swap_detail_formulas_modal"
></button>

<button
  type="button"
  class="hidden"
  aria-hidden="true"
  tabindex="-1"
  data-kt-modal-toggle="#swap_detail_delete_fee_modal"
></button>

<button
  type="button"
  class="hidden"
  aria-hidden="true"
  tabindex="-1"
  data-kt-modal-toggle="#swap_detail_delete_action_modal"
></button>

<button
  type="button"
  class="hidden"
  aria-hidden="true"
  tabindex="-1"
  data-kt-modal-toggle="#swap_detail_delete_note_modal"
></button>
